import React, { useMemo } from 'react'
import UnifiedQuickSearch from './UnifiedQuickSearch'
import { opportunitiesSearchConfig } from '../config/searchConfigs'
import { useList } from '@refinedev/core'

interface OpportunitiesQuickSearchNewProps {
  onSearchChange?: (filters: any) => void
}

export default function OpportunitiesQuickSearchNew({
  onSearchChange,
}: OpportunitiesQuickSearchNewProps) {
  // Fetch regions data for the region autocomplete
  const { data: regionsData, isLoading: regionsLoading } = useList({
    resource: 'regions',
    pagination: { pageSize: 5000, mode: 'off' }, // Increased to handle all 3430+ regions
    sorters: [{ field: 'name', order: 'asc' }],
  })

  // Fetch assigned users data for the assigned_to dropdown
  const { data: assignedUsers } = useList({
    resource: 'assigned-opportunities',
    meta: { get_users: true },
    pagination: { pageSize: 1000 },
  })

  // Get unique assigned users
  const uniqueUsers = useMemo(() => {
    if (!assignedUsers?.data) return []
    const userMap = new Map()
    assignedUsers.data.forEach((user: any) => {
      if (user.uuid && user.name && !userMap.has(user.uuid)) {
        userMap.set(user.uuid, { uuid: user.uuid, name: user.name })
      }
    })
    return Array.from(userMap.values()).sort((a: any, b: any) =>
      a.name.localeCompare(b.name)
    )
  }, [assignedUsers])

  // Create dynamic config with fetched data
  const config = useMemo(() => {
    const dynamicConfig = { ...opportunitiesSearchConfig }

    // Update region options
    if (regionsData?.data) {
      const regionField = dynamicConfig.advancedFilterRows[1]?.fields.find(
        (f) => f.key === 'region_id'
      )
      if (regionField) {
        regionField.options = [
          { value: { id: 'no_region' }, label: 'None' },
          ...regionsData.data.map((region) => ({
            value: region,
            label: region.name,
          })),
        ]
      }
    }

    // Update assigned_to options with unique assigned users
    if (uniqueUsers.length > 0) {
      const assignedToField = dynamicConfig.advancedFilterRows[0]?.fields.find(
        (f) => f.key === 'assigned_to'
      )
      if (assignedToField) {
        assignedToField.options = [
          { value: 'unassigned', label: 'Unassigned' },
          ...uniqueUsers.map((user) => ({
            value: user.uuid, // Store just the user UUID
            label: user.name,
          })),
        ]
      }
    }

    return dynamicConfig
  }, [regionsData, uniqueUsers])

  return (
    <UnifiedQuickSearch
      config={config}
      onSearchChange={onSearchChange}
      resourceName="opportunities"
    />
  )
}
